/**
 * Guest workspace snapshot + claim-after-login.
 * Guests work in the browser; after Google login we POST layers to the backend.
 */

import { API_BASE } from '../config/api';
import { ensureGuestToken, getSessionToken } from '../utils/sessionManager';

const GUEST_LAYERS_KEY = 'geovera_guest_layers_v1';
const GUEST_TOKEN_KEY = 'geovera_guest_token_at_save';
const GUEST_SAVED_AT_KEY = 'geovera_guest_layers_saved_at';
/** Design default: guest local snapshot expires after 14 days */
export const GUEST_SNAPSHOT_TTL_MS = 14 * 24 * 60 * 60 * 1000;

export interface GuestFeaturePayload {
  geometry: { type: string; coordinates: unknown };
  properties?: Record<string, unknown>;
}

export interface GuestLayerPayload {
  sourceLayerId?: string;
  name: string;
  geometryType: 'Point' | 'LineString' | 'Polygon';
  crs?: string;
  symbology?: Record<string, unknown> | null;
  features?: GuestFeaturePayload[];
}

export function getGuestTokenForClaim(): string {
  try {
    const saved = localStorage.getItem(GUEST_TOKEN_KEY);
    if (saved) return saved;
  } catch {
    /* ignore */
  }
  return ensureGuestToken();
}

function isGuestSnapshotExpired(): boolean {
  try {
    const raw = localStorage.getItem(GUEST_SAVED_AT_KEY);
    if (!raw) return false;
    const savedAt = Date.parse(raw);
    if (Number.isNaN(savedAt)) return true;
    return Date.now() - savedAt > GUEST_SNAPSHOT_TTL_MS;
  } catch {
    return false;
  }
}

/** Persist guest layers locally (capped). Call when guest finishes edits / shares. */
export function saveGuestLayersSnapshot(layers: GuestLayerPayload[]): void {
  try {
    const capped = layers.slice(0, 10).map((l) => ({
      ...l,
      features: (l.features || []).slice(0, 500),
    }));
    localStorage.setItem(GUEST_LAYERS_KEY, JSON.stringify(capped));
    localStorage.setItem(GUEST_TOKEN_KEY, ensureGuestToken());
    localStorage.setItem(GUEST_SAVED_AT_KEY, new Date().toISOString());
  } catch (e) {
    console.warn('[guestWorkspace] failed to save snapshot', e);
  }
}

export function loadGuestLayersSnapshot(): GuestLayerPayload[] {
  try {
    if (isGuestSnapshotExpired()) {
      clearGuestLayersSnapshot();
      return [];
    }
    const raw = localStorage.getItem(GUEST_LAYERS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as GuestLayerPayload[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function clearGuestLayersSnapshot(): void {
  try {
    localStorage.removeItem(GUEST_LAYERS_KEY);
    localStorage.removeItem(GUEST_TOKEN_KEY);
    localStorage.removeItem(GUEST_SAVED_AT_KEY);
  } catch {
    /* ignore */
  }
}

function getCsrfToken(): string | null {
  const m = document.cookie.match(/(^|;)\s*_csrf_token\s*=\s*([^;]+)/);
  return m ? m[2] : null;
}

export interface ClaimedGuestLayer {
  id: string;
  name: string;
  sourceLayerId?: string;
}

/**
 * After login: merge guest snapshot into the authenticated account.
 * Idempotent empty success if nothing to claim.
 */
export interface ClaimGuestWorkspaceResult {
  success: boolean;
  claimedLayers: number;
  layers: ClaimedGuestLayer[];
  message?: string;
}

let claimGuestWorkspaceInFlight: Promise<ClaimGuestWorkspaceResult> | null = null;

export function claimGuestWorkspace(): Promise<ClaimGuestWorkspaceResult> {
  if (!claimGuestWorkspaceInFlight) {
    claimGuestWorkspaceInFlight = performGuestWorkspaceClaim();
    void claimGuestWorkspaceInFlight.then(
      () => {
        claimGuestWorkspaceInFlight = null;
      },
      () => {
        claimGuestWorkspaceInFlight = null;
      }
    );
  }
  return claimGuestWorkspaceInFlight;
}

async function performGuestWorkspaceClaim(): Promise<ClaimGuestWorkspaceResult> {
  const layers = loadGuestLayersSnapshot();
  const guestToken = getGuestTokenForClaim();
  const sessionToken = getSessionToken();

  if (!sessionToken || sessionToken.startsWith('guest_')) {
    return { success: false, claimedLayers: 0, layers: [], message: 'Not authenticated' };
  }

  // Nothing local to claim — still ping API so server can log guest token association
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${sessionToken}`,
  };
  const csrf = getCsrfToken();
  if (csrf) headers['X-CSRF-Token'] = csrf;

  try {
    const res = await fetch(`${API_BASE}/session/claim-guest`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ guestToken, layers }),
    });

    const data = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      claimedLayers?: number;
      layers?: ClaimedGuestLayer[];
      message?: string;
    };

    if (res.ok && data.success) {
      clearGuestLayersSnapshot();
      return {
        success: true,
        claimedLayers: data.claimedLayers ?? 0,
        layers: Array.isArray(data.layers) ? data.layers : [],
        message: data.message,
      };
    }

    return {
      success: false,
      claimedLayers: 0,
      layers: [],
      message: data.message || `Claim failed (${res.status})`,
    };
  } catch (e) {
    return {
      success: false,
      claimedLayers: 0,
      layers: [],
      message: e instanceof Error ? e.message : 'Network error',
    };
  }
}

/**
 * Build claim payload from Redux-style layer objects (subset of fields).
 */
export function layersToGuestPayload(
  layers: Array<{
    id?: string;
    name?: string;
    geometryType?: string;
    crs?: string;
    symbology?: Record<string, unknown> | null;
    features?: Array<{
      geometry?: { type: string; coordinates: unknown };
      properties?: Record<string, unknown>;
    }>;
  }>
): GuestLayerPayload[] {
  const allowed = new Set(['Point', 'LineString', 'Polygon']);
  return layers
    .filter((l) => l.geometryType && allowed.has(l.geometryType))
    .map((l) => ({
      sourceLayerId: l.id,
      name: l.name || 'Guest layer',
      geometryType: l.geometryType as GuestLayerPayload['geometryType'],
      crs: l.crs || 'EPSG:4326',
      symbology: l.symbology ?? null,
      features: (l.features || [])
        .filter((f) => f.geometry?.type)
        .map((f) => ({
          geometry: f.geometry as GuestFeaturePayload['geometry'],
          properties: f.properties || {},
        })),
    }));
}
